import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  OperatorResponse,
  OperatorListResponse,
  OperatorCreateRequest,
  OperatorUpdateRequest,
} from '@produzionejb/types';
import { api } from './api-client';

const QK_LIST = ['operators'] as const;

interface ListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  active?: boolean;
  includeLegacy?: boolean;
}

function toQuery(p: ListParams): string {
  const q = new URLSearchParams();
  if (p.page != null) q.set('page', String(p.page));
  if (p.pageSize != null) q.set('pageSize', String(p.pageSize));
  if (p.search) q.set('search', p.search);
  if (p.active !== undefined) q.set('active', String(p.active));
  if (p.includeLegacy !== undefined) q.set('includeLegacy', String(p.includeLegacy));
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function useOperators(params: ListParams = {}) {
  return useQuery({
    queryKey: [...QK_LIST, params],
    queryFn: () => api<OperatorListResponse>(`/api/operators${toQuery(params)}`),
  });
}

export function useOperator(id: string | null) {
  return useQuery({
    queryKey: ['operator', id],
    queryFn: () => api<OperatorResponse>(`/api/operators/${id}`),
    enabled: !!id,
  });
}

export function useCreateOperator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: OperatorCreateRequest) =>
      api<OperatorResponse>('/api/operators', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_LIST }),
  });
}

export function useUpdateOperator(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: OperatorUpdateRequest) =>
      api<OperatorResponse>(`/api/operators/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_LIST });
      qc.invalidateQueries({ queryKey: ['operator', id] });
    },
  });
}

export function useDeleteOperator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/operators/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_LIST }),
  });
}
